// Pop-up da reunião semanal (só admin). Monta a pauta (projetos em atenção +
// parados), permite comentar/encaminhar cada projeto, tratar assuntos avulsos
// (fios de discussão) e trazer qualquer projeto/assunto para a pauta. "Fechar"
// mantém o que já foi registrado sem gerar ata; "Encerrar" salva a ata.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addNote, updateNote, loadNotes } from '../lib/notes';
import { uploadEvidencia } from '../lib/photos';
import { loadPeople, type Person } from '../lib/notifications';
import {
  createDiscussion,
  createEncaminhamentoMulti,
  loadAssuntos,
  loadOpenEncaminhamentos,
  reopenDiscussion,
  type Discussion,
} from '../lib/encaminhamentos';
import {
  saveMeeting,
  loadMeetingDraft,
  saveMeetingDraft,
  clearMeetingDraft,
  collectMeetingActivity,
  type MeetingItem,
} from '../lib/meetings';
import { DiscussionThread } from './DiscussionThread';
import { StatusBadge, desvioLabel, fmtDate } from './ui';
import type { ProductSummary } from '../shared/types';

function anchorStage(s: ProductSummary): number {
  return s.proximaEtapa?.n ?? s.stages.find((c) => c.status !== 'concluido')?.n ?? s.stages[0]?.n ?? 1;
}

// Agrupa encaminhamentos pelo produto, para manter tudo do mesmo item junto na
// pauta (vários fios do mesmo produto aparecem embaixo do mesmo cabeçalho).
// Projetos com nome em ordem alfabética; sem projeto vinculado por último.
interface EncGrupo {
  key: string;
  productId: string | null;
  productName: string | null;
  fios: Discussion[];
}
function groupEncByProduct(list: Discussion[]): EncGrupo[] {
  const groups = new Map<string, Discussion[]>();
  for (const d of list) {
    const key = d.productId ?? '__none__';
    const arr = groups.get(key) ?? [];
    arr.push(d);
    groups.set(key, arr);
  }
  const out: EncGrupo[] = [...groups.entries()].map(([key, fios]) => ({
    key,
    productId: fios[0]?.productId ?? null,
    productName: fios[0]?.productName ?? null,
    fios,
  }));
  out.sort((a, b) => {
    if (!a.productId && b.productId) return 1;
    if (a.productId && !b.productId) return -1;
    return (a.productName ?? '').localeCompare(b.productName ?? '');
  });
  return out;
}

export function ReuniaoSemanal({
  orgId,
  userId,
  fullName,
  isAdmin,
  summaries,
  onClose,
}: {
  orgId: string;
  userId: string | null;
  fullName: string | null;
  isAdmin: boolean;
  summaries: ProductSummary[];
  onClose: () => void;
}) {
  const nav = useNavigate();
  const [people, setPeople] = useState<Person[]>([]);
  const [openEnc, setOpenEnc] = useState<Discussion[]>([]);
  const [assuntos, setAssuntos] = useState<Discussion[]>([]);
  const [dispensados, setDispensados] = useState<Set<string>>(new Set());
  const [incluidos, setIncluidos] = useState<Set<string>>(new Set());
  const [fiosFora, setFiosFora] = useState<Set<string>>(new Set());
  const [startedAt, setStartedAt] = useState<string | null>(null);
  const [log, setLog] = useState<MeetingItem[]>([]);
  const [encerrando, setEncerrando] = useState(false);
  const [fechando, setFechando] = useState(false);
  const [carregado, setCarregado] = useState(false);

  const reloadOpen = () => loadOpenEncaminhamentos(orgId).then(setOpenEnc);
  const reloadAssuntos = () => loadAssuntos(orgId, { includeClosed: true }).then(setAssuntos);
  useEffect(() => {
    loadPeople(orgId).then(setPeople);
    reloadOpen();
    reloadAssuntos();
    // Restaura o rascunho da pauta (o que já foi tirado/incluído + registros).
    loadMeetingDraft(orgId).then((d) => {
      if (d) {
        setDispensados(new Set(d.dispensados));
        setIncluidos(new Set(d.incluidos));
        setFiosFora(new Set(d.fiosForaPauta));
        setLog(d.itens);
      }
      // Marca o início da reunião (janela da ata). Mantém o de um rascunho
      // existente; se não houver, começa agora.
      setStartedAt(d?.startedAt ?? new Date().toISOString());
      setCarregado(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const addLog = (item: MeetingItem) => setLog((l) => [...l, item]);

  const atencao = useMemo(
    () =>
      summaries
        .filter((s) => s.lifecycle === 'ativo' && (s.status === 'atrasado' || s.status === 'atencao'))
        .sort((a, b) => b.desvioDias - a.desvioDias),
    [summaries],
  );
  const parados = useMemo(
    () =>
      summaries
        .filter(
          (s) =>
            s.lifecycle === 'ativo' &&
            s.status !== 'concluido' &&
            s.status !== 'atrasado' &&
            s.status !== 'atencao' &&
            !s.stages.some((c) => c.status === 'em_andamento'),
        )
        .sort((a, b) => `${a.brand}${a.name}`.localeCompare(`${b.brand}${b.name}`)),
    [summaries],
  );
  const autoIds = useMemo(() => new Set([...atencao, ...parados].map((s) => s.productId)), [atencao, parados]);
  const byId = useMemo(() => new Map(summaries.map((s) => [s.productId, s])), [summaries]);

  const naPauta = atencao.filter((s) => !dispensados.has(s.productId));
  const paradosNaPauta = parados.filter((s) => !dispensados.has(s.productId));
  const incluidosCards = [...incluidos]
    .map((id) => byId.get(id))
    .filter(
      (s): s is ProductSummary =>
        Boolean(s) && !dispensados.has(s!.productId) && !autoIds.has(s!.productId),
    );

  // Rodapé: todos os projetos (menos cancelados) que NÃO estão na pauta agora —
  // inclui os que foram "tirados da pauta", para poder reincluir.
  const noTopo = useMemo(
    () => new Set([...naPauta, ...paradosNaPauta, ...incluidosCards].map((s) => s.productId)),
    [naPauta, paradosNaPauta, incluidosCards],
  );
  const outrosProjetos = useMemo(
    () =>
      summaries
        .filter((s) => s.lifecycle !== 'cancelado' && !noTopo.has(s.productId))
        .sort((a, b) => `${a.brand}${a.name}`.localeCompare(`${b.brand}${b.name}`)),
    [summaries, noTopo],
  );

  // Tirar da pauta: some do topo e cai na lista de baixo (remove de incluídos).
  function dispensar(id: string) {
    setDispensados((d) => new Set(d).add(id));
    setIncluidos((x) => {
      const n = new Set(x);
      n.delete(id);
      return n;
    });
  }
  // Incluir na pauta: volta ao topo (tira de dispensados; marca como incluído se
  // não for um item automático de atenção/parado).
  function incluir(id: string) {
    setDispensados((d) => {
      const n = new Set(d);
      n.delete(id);
      return n;
    });
    if (!autoIds.has(id)) setIncluidos((x) => new Set(x).add(id));
  }

  const assuntosAbertos = assuntos.filter((a) => a.status === 'aberto');
  const assuntosFechados = assuntos.filter((a) => a.status === 'encerrado');

  // Fios na pauta de hoje (tirando os parkeados) e os que estão "fora da pauta".
  const encNaPauta = openEnc.filter((e) => !fiosFora.has(e.id));
  const assuntosNaPauta = assuntosAbertos.filter((a) => !fiosFora.has(a.id));
  const encGrupos = groupEncByProduct(encNaPauta);
  const foraDaPauta = [...openEnc, ...assuntosAbertos].filter((x) => fiosFora.has(x.id));

  const dataHoje = new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });

  const draftState = () => ({
    dispensados: [...dispensados],
    incluidos: [...incluidos],
    fiosForaPauta: [...fiosFora],
    startedAt,
    itens: log,
  });

  // Tirar / voltar fios (encaminhamentos e assuntos) da pauta de hoje.
  function tirarFio(id: string) {
    setFiosFora((f) => new Set(f).add(id));
  }
  function voltarFio(id: string) {
    setFiosFora((f) => {
      const n = new Set(f);
      n.delete(id);
      return n;
    });
  }

  // Autosave: a cada mudança na pauta (depois de carregado) guarda o rascunho,
  // para não perder nada mesmo se a janela for fechada sem clicar em nada.
  useEffect(() => {
    if (!carregado) return;
    const t = setTimeout(() => {
      saveMeetingDraft({ orgId, updatedById: userId, draft: draftState() });
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregado, dispensados, incluidos, fiosFora, startedAt, log]);

  // Fechar sem gerar ata: mantém todas as movimentações salvas para continuar depois.
  async function fecharSemAta() {
    if (fechando) return;
    setFechando(true);
    await saveMeetingDraft({ orgId, updatedById: userId, draft: draftState() });
    setFechando(false);
    onClose();
  }

  async function encerrar() {
    setEncerrando(true);
    // Ata completa: tudo que foi comentado/editado/tratado desde o início da
    // reunião (persistido), não só o que passou pelo card. Se por algum motivo
    // não houver janela, cai no log em memória.
    let itens: MeetingItem[] = log;
    if (startedAt) {
      const persistido = await collectMeetingActivity(orgId, startedAt);
      if (persistido.length > 0) itens = persistido;
    }
    if (itens.length > 0) {
      await saveMeeting({ orgId, createdById: userId, createdByName: fullName, itens });
    }
    // Ata gerada → limpa o rascunho para começar do zero na próxima.
    await clearMeetingDraft(orgId);
    setEncerrando(false);
    onClose();
  }

  const cardProps = (s: ProductSummary) => ({
    s,
    orgId,
    userId,
    fullName,
    people,
    onLogged: addLog,
    onDispensar: () => dispensar(s.productId),
    onGoProject: () => nav(`/produto/${s.productId}`),
  });

  return (
    /* Não fecha ao clicar/arrastar no fundo — só pelo botão de cima ou ao gerar a ata. */
    <div className="modal-backdrop">
      <div className="modal card reuniao" onClick={(e) => e.stopPropagation()}>
        <div className="section-title">
          <h2>Reunião semanal — {dataHoje}</h2>
          <button
            className="btn-ghost btn-sm"
            disabled={fechando}
            onClick={fecharSemAta}
            title="Fecha sem gerar ata — tudo que você mexeu fica salvo para continuar depois"
          >
            {fechando ? 'Salvando…' : 'Fechar sem gerar ata'}
          </button>
        </div>
        <p className="muted" style={{ fontSize: 12.5, marginTop: -4 }}>
          Comentar um projeto grava no “Comentários &amp; evidências” dele. Marcar pessoas (@) gera
          encaminhamentos (fios de discussão). Ao <b>fechar sem gerar ata</b>, tudo que você mexeu
          fica salvo e a reunião continua deste ponto quando reabrir. Ao <b>encerrar</b>, a ata fica
          salva em “Reuniões” e a pauta recomeça do zero.
        </p>

        {encNaPauta.length > 0 && (
          <div className="reuniao-sec">
            <h3>📌 Encaminhamentos abertos ({encNaPauta.length})</h3>
            {encGrupos.map((g) => (
              <div key={g.key} className="reuniao-prodgrupo">
                {g.productId ? (
                  <button
                    className="linklike reuniao-prodgrupo-tit"
                    onClick={() => nav(`/produto/${g.productId}`)}
                    title="Abrir o projeto"
                  >
                    {g.productName ?? 'Projeto'} ›
                  </button>
                ) : (
                  <div className="reuniao-prodgrupo-tit muted">Sem projeto vinculado</div>
                )}
                {g.fios.map((e) => {
                  const s = e.productId ? byId.get(e.productId) : undefined;
                  return (
                    <DiscussionThread
                      key={e.id}
                      d={e}
                      userId={userId}
                      fullName={fullName}
                      isAdmin={isAdmin}
                      anchorN={s ? anchorStage(s) : undefined}
                      onChanged={reloadOpen}
                      showProjectLink={false}
                      onTirarPauta={() => tirarFio(e.id)}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        )}

        <div className="reuniao-sec">
          <h3>🔴 Projetos em atenção ({naPauta.length})</h3>
          {atencao.length === 0 && <p className="muted">Nenhum projeto atrasado ou em atenção. 🎉</p>}
          {naPauta.map((s) => (
            <ProjetoCard key={s.productId} {...cardProps(s)} />
          ))}
        </div>

        {paradosNaPauta.length > 0 && (
          <div className="reuniao-sec">
            <h3>🟡 Parados / sem movimento ({paradosNaPauta.length})</h3>
            {paradosNaPauta.map((s) => (
              <ProjetoCard key={s.productId} {...cardProps(s)} />
            ))}
          </div>
        )}

        {incluidosCards.length > 0 && (
          <div className="reuniao-sec">
            <h3>➕ Incluídos na pauta ({incluidosCards.length})</h3>
            {incluidosCards.map((s) => (
              <ProjetoCard key={s.productId} {...cardProps(s)} />
            ))}
          </div>
        )}

        <div className="reuniao-sec">
          <h3>🗂️ Outros assuntos (fora de projetos)</h3>
          <NovoAssunto
            orgId={orgId}
            userId={userId}
            fullName={fullName}
            people={people}
            onCreated={(titulo, resp, coment) => {
              addLog({ tipo: 'outro', assunto: titulo, responsavel: resp, comentario: coment });
              reloadAssuntos();
            }}
          />
          {assuntosNaPauta.map((a) => (
            <DiscussionThread
              key={a.id}
              d={a}
              userId={userId}
              fullName={fullName}
              isAdmin={isAdmin}
              onChanged={reloadAssuntos}
              onTirarPauta={() => tirarFio(a.id)}
            />
          ))}
        </div>

        {foraDaPauta.length > 0 && (
          <div className="reuniao-sec">
            <h3>😴 Fora da pauta de hoje ({foraDaPauta.length})</h3>
            <p className="muted" style={{ fontSize: 12, marginTop: -4 }}>
              Continuam abertos — só não entram na pauta de hoje. Traga de volta quando quiser.
            </p>
            <div className="andamento-list">
              {foraDaPauta.map((x) => (
                <div className="andamento-row" key={x.id}>
                  <span className="seal pend">{x.kind === 'assunto' ? 'assunto' : 'aberto'}</span>
                  <span className="andamento-nome">
                    {x.productName ? `${x.productName} · ` : ''}
                    {x.title}
                  </span>
                  <button
                    className="btn-ghost btn-sm"
                    style={{ marginLeft: 'auto' }}
                    onClick={() => voltarFio(x.id)}
                  >
                    voltar pra pauta ↑
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {outrosProjetos.length > 0 && (
          <div className="reuniao-sec">
            <h3>Todos os projetos ({outrosProjetos.length})</h3>
            <p className="muted" style={{ fontSize: 12, marginTop: -4 }}>
              Em andamento, pausados e em espera (cancelados não aparecem). “Incluir na pauta” só nos
              que quiser tratar — o que você não mexer não entra na ata.
            </p>
            <div className="andamento-list">
              {outrosProjetos.map((s) => (
                <div className="andamento-row" key={s.productId}>
                  <StatusBadge status={s.status} />
                  <button className="linklike andamento-nome" onClick={() => nav(`/produto/${s.productId}`)}>
                    {s.brand} · {s.name}
                  </button>
                  <span className="muted andamento-fase">
                    {s.faseAtual}
                    {s.proximaEtapa ? ` · ${s.proximaEtapa.name}` : ''}
                  </span>
                  <button className="btn-ghost btn-sm" onClick={() => incluir(s.productId)}>
                    incluir na pauta ↑
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {assuntosFechados.length > 0 && (
          <div className="reuniao-sec">
            <h3>Outros assuntos encerrados ({assuntosFechados.length})</h3>
            <div className="andamento-list">
              {assuntosFechados.map((a) => (
                <div className="andamento-row" key={a.id}>
                  <span className="seal ok">encerrado</span>
                  <span className="andamento-nome">{a.title}</span>
                  {isAdmin && (
                    <button
                      className="btn-ghost btn-sm"
                      style={{ marginLeft: 'auto' }}
                      onClick={async () => {
                        await reopenDiscussion(a);
                        reloadAssuntos();
                      }}
                    >
                      reabrir e tratar ↑
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="reuniao-footer">
          <span className="muted" style={{ fontSize: 12 }}>
            {log.length > 0 ? `${log.length} registro(s) nesta reunião` : 'Nada registrado ainda'}
          </span>
          <button className="btn-primary" disabled={encerrando} onClick={encerrar}>
            {encerrando ? 'Salvando ata…' : 'Encerrar reunião (gera ata)'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MentionChips({
  people,
  selected,
  onToggle,
}: {
  people: Person[];
  selected: string[];
  onToggle: (id: string) => void;
}) {
  if (people.length === 0) return null;
  return (
    <div className="note-mentions">
      <span className="muted" style={{ fontSize: 12 }}>Marcar:</span>
      {people.map((p) => (
        <button
          key={p.id}
          type="button"
          className={`chip ${selected.includes(p.id) ? 'on' : ''}`}
          onClick={() => onToggle(p.id)}
        >
          @{p.name}
        </button>
      ))}
    </div>
  );
}

function ProjetoCard({
  s,
  orgId,
  userId,
  fullName,
  people,
  onLogged,
  onDispensar,
  onGoProject,
}: {
  s: ProductSummary;
  orgId: string;
  userId: string | null;
  fullName: string | null;
  people: Person[];
  onLogged: (item: MeetingItem) => void;
  onDispensar: () => void;
  onGoProject: () => void;
}) {
  const [body, setBody] = useState('');
  const [mentions, setMentions] = useState<string[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  // Comentário que ESTE usuário já postou HOJE neste projeto — editamos ele em vez
  // de empilhar vários comentários no mesmo item no mesmo dia.
  const [todayNoteId, setTodayNoteId] = useState<string | null>(null);
  const [todayMentions, setTodayMentions] = useState<string[]>([]);
  const n = anchorStage(s);
  const label = `${s.brand} · ${s.name}`;

  useEffect(() => {
    let alive = true;
    loadNotes(s.productId).then((ns) => {
      if (!alive) return;
      const hoje = new Date().toISOString().slice(0, 10);
      const mine = ns.find(
        (nt) => nt.authorId === userId && nt.n === n && nt.createdAt.slice(0, 10) === hoje,
      );
      if (mine) {
        setTodayNoteId(mine.id);
        setBody(mine.body ?? '');
        setTodayMentions(mine.mentionedIds ?? []);
      }
    });
    return () => {
      alive = false;
    };
  }, [s.productId, userId, n]);

  const toggle = (id: string) =>
    setMentions((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));

  async function registrar() {
    if (!body.trim() && !file) return;
    setBusy(true);
    let attachmentUrl: string | null = null;
    let attachmentName: string | null = null;
    if (file) {
      const up = await uploadEvidencia(orgId, s.productId, n, file);
      if (up.error) {
        alert(up.error);
        setBusy(false);
        return;
      }
      attachmentUrl = up.url ?? null;
      attachmentName = up.name ?? null;
    }
    // Novas menções (só as que ainda não viraram encaminhamento hoje) geram fio.
    const novasMentions = mentions.filter((m) => !todayMentions.includes(m));

    let noteId = todayNoteId;
    if (todayNoteId && !file) {
      // Já há um comentário meu hoje: edito ele (não empilha outro).
      const { error } = await updateNote(todayNoteId, {
        body: body.trim() || null,
        mentionedIds: [...new Set([...todayMentions, ...mentions])],
      });
      if (error) {
        alert(error);
        setBusy(false);
        return;
      }
    } else {
      const { error, id } = await addNote({
        orgId,
        productId: s.productId,
        n,
        authorId: userId,
        authorName: fullName,
        body: body.trim() || null,
        attachmentUrl,
        attachmentName,
        mentionedIds: mentions,
      });
      if (error) {
        alert(error);
        setBusy(false);
        return;
      }
      noteId = id ?? null;
      setTodayNoteId(noteId);
    }

    // UM encaminhamento com TODOS os marcados (um fio só; todos comentam).
    const marcados = novasMentions.map((pid) => ({
      id: pid,
      name: people.find((p) => p.id === pid)?.name ?? null,
    }));
    const marcadosNomes = marcados.map((m) => m.name ?? '—');
    if (marcados.length > 0) {
      await createEncaminhamentoMulti({
        orgId,
        productId: s.productId,
        title: body.trim() || 'Verificar item da reunião',
        createdById: userId,
        createdByName: fullName,
        participants: marcados,
      });
    }
    setTodayMentions((prev) => [...new Set([...prev, ...mentions])]);
    onLogged({
      tipo: 'projeto',
      projeto: label,
      productId: s.productId,
      comentario: body.trim() || null,
      anexo: attachmentName,
      marcados: marcadosNomes,
    });
    setBusy(false);
    setDone(
      novasMentions.length
        ? `Registrado · ${novasMentions.length} encaminhamento(s).`
        : todayNoteId
          ? 'Comentário de hoje atualizado.'
          : 'Comentário registrado no projeto.',
    );
    setMentions([]);
    setFile(null);
  }

  return (
    <div className="reuniao-card">
      <div className="reuniao-card-head">
        <StatusBadge status={s.status} />
        <button className="linklike" onClick={onGoProject}>
          {label} ›
        </button>
        <span className="muted" style={{ fontSize: 12 }}>
          {s.faseAtual} · {desvioLabel(s.desvioDias)} · previsão {fmtDate(s.previsaoConclusao)}
        </span>
        <button className="btn-ghost btn-sm" style={{ marginLeft: 'auto' }} title="Não tratar hoje" onClick={onDispensar}>
          ✕ tirar da pauta
        </button>
      </div>
      {s.proximaEtapa && (
        <div className="muted" style={{ fontSize: 12.5, margin: '2px 0 6px' }}>
          Próxima etapa: <b>{s.proximaEtapa.name}</b>
        </div>
      )}
      {done ? (
        <div className="ok" style={{ fontSize: 13, padding: '6px 0' }}>✓ {done}</div>
      ) : (
        <>
          <textarea
            className="note-textarea"
            rows={2}
            placeholder="Comentário / encaminhamento sobre este projeto…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <MentionChips people={people} selected={mentions} onToggle={toggle} />
          <div className="reuniao-actions">
            <label className="note-clip" title="Anexar arquivo">
              📎 {file ? file.name : 'Anexo'}
              <input type="file" style={{ display: 'none' }} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </label>
            <button className="btn-primary btn-sm" disabled={busy || (!body.trim() && !file)} onClick={registrar}>
              {busy ? 'Salvando…' : todayNoteId ? 'Atualizar comentário de hoje' : 'Registrar'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function NovoAssunto({
  orgId,
  userId,
  fullName,
  people,
  onCreated,
}: {
  orgId: string;
  userId: string | null;
  fullName: string | null;
  people: Person[];
  onCreated: (titulo: string, responsavel: string | null, comentario: string) => void;
}) {
  const [nome, setNome] = useState('');
  const [coment, setComent] = useState('');
  const [pid, setPid] = useState('');
  const [busy, setBusy] = useState(false);

  async function criar() {
    if (!nome.trim()) return;
    setBusy(true);
    const person = people.find((p) => p.id === pid);
    const { error } = await createDiscussion({
      orgId,
      kind: 'assunto',
      title: nome.trim(),
      productId: null,
      assignedToId: pid || null,
      assignedName: person?.name ?? null,
      createdById: userId,
      createdByName: fullName,
      firstMessage: coment.trim() || nome.trim(),
    });
    setBusy(false);
    if (error) return alert(error);
    onCreated(nome.trim(), person?.name ?? null, coment.trim());
    setNome('');
    setComent('');
    setPid('');
  }

  return (
    <div className="novo-assunto">
      <p className="muted" style={{ fontSize: 12, margin: '0 0 6px' }}>
        Dê um nome ao assunto (ex.: “MÁQUINA DE ENVASE”) e comece a conversa. Ele fica salvo no menu
        “Outros assuntos” com todo o histórico, até você encerrar (admin reabre).
      </p>
      <div className="reuniao-outros">
        <input placeholder="Nome do assunto…" value={nome} onChange={(e) => setNome(e.target.value)} />
        <select value={pid} onChange={(e) => setPid(e.target.value)}>
          <option value="">Responsável (opcional)…</option>
          {people.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>
      <textarea
        className="note-textarea"
        rows={2}
        placeholder="Primeiro comentário do assunto…"
        value={coment}
        onChange={(e) => setComent(e.target.value)}
        style={{ marginTop: 6 }}
      />
      <div className="reuniao-actions">
        <button className="btn-ghost btn-sm" disabled={busy || !nome.trim()} onClick={criar}>
          + Criar assunto
        </button>
      </div>
    </div>
  );
}
